import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import {
  FaPhone,
  FaStar,
  FaCheckCircle,
  FaHardHat,
  FaHome,
  FaSwimmingPool,
  FaTools,
  FaMapMarkerAlt,
  FaClock,
  FaShieldAlt,
} from "react-icons/fa";
import "./LandingPage.css";

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const body = new URLSearchParams({
      "form-name": "quote-request",
      ...formData,
    }).toString();
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    })
      .then(() => {
        alert("Thank you! We will contact you shortly.");
        setFormData({ name: "", phone: "", email: "", message: "" });
      })
      .catch(() => {
        alert("Something went wrong. Please call us at (818) 220-7356.");
      });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      <Helmet>
        <title>
          Demo Guys - #1 Demolition Services Los Angeles | Licensed & Insured
        </title>
        <meta
          name="description"
          content="Professional demolition services in Los Angeles. Pool demolition with permitting, residential & commercial demolition, excavation, grading, asbestos abatement. Licensed & insured, 20+ years experience. Call (818) 220-7356!"
        />
        <meta
          name="keywords"
          content="demolition Los Angeles, pool demolition LA, asbestos abatement, excavation services, grading, demolition contractors, residential demolition, commercial demolition, San Fernando Valley demolition"
        />
        <link rel="canonical" href="https://demoguys.net/" />
      </Helmet>

      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img
                src="/images/logo.png"
                alt="Demo N Hauling Guys Logo"
                className="logo-img"
              />
            </div>
            <nav className="nav">
              <a href="#services">Services</a>
              <a href="#about">About</a>
              <a href="#contact">Contact</a>
              <a href="tel:8182207356" className="phone-link">
                <FaPhone /> (818) 220-7356
              </a>
            </nav>
            <div className="mobile-cta">
              <a href="tel:8182207356" className="btn btn-primary">
                <FaPhone /> Call Now
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" itemScope itemType="https://schema.org/Service">
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <div className={`hero-text ${isVisible ? "fade-in-up" : ""}`}>
              <div className="hero-badge">
                <span className="badge-text">
                  Best Demolition Contractors in Los Angeles
                </span>
              </div>

              <h1 itemProp="name">
                Los Angeles' #1 Professional Demolition Experts
              </h1>

              <p className="hero-subtitle" itemProp="description">
                Expert Demolition Services for Residential & Commercial
                Properties. Pool Demolition with Permitting, Excavation,
                Grading, and Asbestos Abatement. Licensed, Insured, and Trusted
                for Over 20 Years in Los Angeles.
              </p>

              <div className="hero-features">
                <div className="feature-badge">
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <FaStar />
                  <span>5.0 Star Rated</span>
                </div>
                <div className="feature-badge">
                  <FaShieldAlt />
                  <span>Licensed & Insured</span>
                </div>
                <div className="feature-badge">
                  <FaClock />
                  <span>Same-Day Service</span>
                </div>
              </div>

              <div className="hero-buttons">
                <a href="tel:8182207356" className="btn btn-primary btn-large">
                  <FaPhone /> Get A Free Quote: (818) 220-7356
                </a>
                <a href="#contact" className="btn btn-secondary btn-large">
                  Request Callback
                </a>
              </div>
            </div>

            <div className="hero-image">
              <img
                src="/images/hero-demolition.jpg"
                alt="Professional Demolition Contractor in Los Angeles"
                className="hero-img"
              />
              <div className="image-badge">
                <p>Speak To Our Demolition Experts for Free Quote</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="reviews-section">
        <div className="container">
          <div className="reviews-content">
            <div className="review-badges">
              <img
                src="/images/google-reviews.svg"
                alt="Google 5 Star Reviews"
                className="review-badge-img"
              />
              <img
                src="/images/yelp-reviews.svg"
                alt="Yelp 5 Star Reviews"
                className="review-badge-img"
              />
            </div>
            <div className="review-text">
              <h3>Trusted by Hundreds of Satisfied Customers</h3>
              <p>
                5-star rated on Google and Yelp. Our customers love our
                professional demolition services, fair pricing, and commitment
                to excellence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item fade-in-up">
              <h3>20+</h3>
              <p>Years Experience</p>
            </div>
            <div className="stat-item fade-in-up">
              <h3>500+</h3>
              <p>Projects Completed</p>
            </div>
            <div className="stat-item fade-in-up">
              <h3>5.0</h3>
              <p>Star Rating</p>
            </div>
            <div className="stat-item fade-in-up">
              <h3>100%</h3>
              <p>Satisfaction Guaranteed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Our Professional Demolition Services</h2>
            <p>
              Complete Demolition Solutions for Residential & Commercial
              Properties in Los Angeles
            </p>
          </div>

          <div className="services-grid">
            {/* Residential Demolition */}
            <article
              className="service-card"
              itemScope
              itemType="https://schema.org/Service"
            >
              <div className="service-image">
                <img
                  src="/images/residential-demo.jpg"
                  alt="Residential Demolition Services"
                />
              </div>
              <div className="service-icon">
                <FaHome />
              </div>
              <h3 itemProp="name">Residential Demolition</h3>
              <p itemProp="description">
                Expert demolition services for homes, garages, sheds, and all
                residential structures. We handle everything from small
                renovations to complete tear-downs safely and efficiently.
              </p>
              <ul className="service-features">
                <li>
                  <FaCheckCircle /> House Demolition
                </li>
                <li>
                  <FaCheckCircle /> Garage Removal
                </li>
                <li>
                  <FaCheckCircle /> Shed Demolition
                </li>
                <li>
                  <FaCheckCircle /> Deck Removal
                </li>
                <li>
                  <FaCheckCircle /> Interior Demolition
                </li>
              </ul>
            </article>

            {/* Commercial Demolition */}
            <article
              className="service-card"
              itemScope
              itemType="https://schema.org/Service"
            >
              <div className="service-image">
                <img
                  src="/images/commercial-demo.jpg"
                  alt="Commercial Demolition Services"
                />
              </div>
              <div className="service-icon">
                <FaTools />
              </div>
              <h3 itemProp="name">Commercial Demolition</h3>
              <p itemProp="description">
                Professional commercial demolition for businesses, warehouses,
                and industrial properties. We minimize downtime and ensure
                safety throughout the entire demolition process.
              </p>
              <ul className="service-features">
                <li>
                  <FaCheckCircle /> Office Buildings
                </li>
                <li>
                  <FaCheckCircle /> Warehouses
                </li>
                <li>
                  <FaCheckCircle /> Retail Spaces
                </li>
                <li>
                  <FaCheckCircle /> Industrial Sites
                </li>
                <li>
                  <FaCheckCircle /> Restaurant Demolition
                </li>
              </ul>
            </article>

            {/* Pool Demolition */}
            <article
              className="service-card service-card-featured"
              itemScope
              itemType="https://schema.org/Service"
            >
              <div className="service-image">
                <img
                  src="/images/pool-backyard-demo.jpg"
                  alt="Pool Demolition with Permitting"
                />
              </div>
              <div className="service-icon">
                <FaSwimmingPool />
              </div>
              <h3 itemProp="name">Pool Demolition & Permitting</h3>
              <p itemProp="description">
                Complete pool demolition services including all necessary
                permits and inspections. We handle the entire process from
                permit application to final inspection and backfill.
              </p>
              <ul className="service-features">
                <li>
                  <FaCheckCircle /> Full Pool Demolition
                </li>
                <li>
                  <FaCheckCircle /> Permit Processing & Filing
                </li>
                <li>
                  <FaCheckCircle /> City Inspections Coordination
                </li>
                <li>
                  <FaCheckCircle /> Proper Backfill & Compaction
                </li>
                <li>
                  <FaCheckCircle /> Spa & Hot Tub Removal
                </li>
              </ul>
            </article>

            {/* Excavation & Grading */}
            <article
              className="service-card"
              itemScope
              itemType="https://schema.org/Service"
            >
              <div className="service-icon">
                <FaHardHat />
              </div>
              <h3 itemProp="name">Excavation & Grading</h3>
              <p itemProp="description">
                Professional excavation and grading services for residential and
                commercial projects. Site preparation, trenching, and land
                leveling for construction projects.
              </p>
              <ul className="service-features">
                <li>
                  <FaCheckCircle /> Site Excavation
                </li>
                <li>
                  <FaCheckCircle /> Land Grading
                </li>
                <li>
                  <FaCheckCircle /> Trenching Services
                </li>
                <li>
                  <FaCheckCircle /> Foundation Preparation
                </li>
                <li>
                  <FaCheckCircle /> Drainage Solutions
                </li>
              </ul>
            </article>

            {/* Asbestos Abatement */}
            <article
              className="service-card"
              itemScope
              itemType="https://schema.org/Service"
            >
              <div className="service-icon">
                <FaShieldAlt />
              </div>
              <h3 itemProp="name">Asbestos Abatement</h3>
              <p itemProp="description">
                Safe and compliant asbestos removal and abatement services.
                Certified professionals handling hazardous materials with full
                regulatory compliance.
              </p>
              <ul className="service-features">
                <li>
                  <FaCheckCircle /> Asbestos Testing
                </li>
                <li>
                  <FaCheckCircle /> Safe Asbestos Removal
                </li>
                <li>
                  <FaCheckCircle /> Certified Abatement
                </li>
                <li>
                  <FaCheckCircle /> Proper Disposal
                </li>
                <li>
                  <FaCheckCircle /> Regulatory Compliance
                </li>
              </ul>
            </article>

            {/* General Abatement */}
            <article
              className="service-card"
              itemScope
              itemType="https://schema.org/Service"
            >
              <div className="service-icon">
                <FaTools />
              </div>
              <h3 itemProp="name">Environmental Abatement</h3>
              <p itemProp="description">
                Complete environmental abatement services for lead paint, mold,
                and other hazardous materials. Ensuring safe removal and
                disposal with full compliance.
              </p>
              <ul className="service-features">
                <li>
                  <FaCheckCircle /> Lead Paint Abatement
                </li>
                <li>
                  <FaCheckCircle /> Mold Remediation
                </li>
                <li>
                  <FaCheckCircle /> Hazmat Removal
                </li>
                <li>
                  <FaCheckCircle /> Site Decontamination
                </li>
                <li>
                  <FaCheckCircle /> Environmental Testing
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="about" className="why-choose-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Why Choose Our Demolition Experts?</h2>
            <p>
              Local Owned and Operated Business Serving Los Angeles Since 2003
            </p>
          </div>

          <div className="why-choose-content">
            <div className="why-choose-text">
              <div className="benefits-list">
                <div className="benefit-item">
                  <FaCheckCircle className="benefit-icon" />
                  <div>
                    <h4>Licensed & Fully Insured</h4>
                    <p>
                      Complete peace of mind with proper licensing and
                      comprehensive insurance coverage for all demolition
                      projects
                    </p>
                  </div>
                </div>

                <div className="benefit-item">
                  <FaCheckCircle className="benefit-icon" />
                  <div>
                    <h4>20+ Years of Experience</h4>
                    <p>
                      Two decades of expertise serving Los Angeles, Ventura
                      County, and surrounding communities
                    </p>
                  </div>
                </div>

                <div className="benefit-item">
                  <FaCheckCircle className="benefit-icon" />
                  <div>
                    <h4>Pool Demolition Specialists</h4>
                    <p>
                      Expert pool demolition with complete permit processing and
                      city inspection coordination
                    </p>
                  </div>
                </div>

                <div className="benefit-item">
                  <FaCheckCircle className="benefit-icon" />
                  <div>
                    <h4>Same Day Services Available</h4>
                    <p>
                      Fast response times with same-day service options for
                      urgent demolition projects
                    </p>
                  </div>
                </div>

                <div className="benefit-item">
                  <FaCheckCircle className="benefit-icon" />
                  <div>
                    <h4>No Hassle Demolition Process Guaranteed</h4>
                    <p>
                      We handle permits, inspections, and all paperwork - you
                      just sit back and relax
                    </p>
                  </div>
                </div>

                <div className="benefit-item">
                  <FaCheckCircle className="benefit-icon" />
                  <div>
                    <h4>Certified Asbestos Abatement</h4>
                    <p>
                      Fully certified and compliant hazardous material removal
                      and environmental abatement services
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="why-choose-image">
              <img
                src="/images/team-interior-demo.jpg"
                alt="Demo N Hauling Guys Team"
                className="why-img"
              />
              <div className="image-overlay">
                <h3>Professional Demolition Team</h3>
                <p>Serving Los Angeles & Ventura County</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="service-areas-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Serving Los Angeles & Surrounding Areas</h2>
            <p>
              Professional demolition services throughout Southern California
            </p>
          </div>

          <div className="areas-grid">
            <div className="area-item">
              <FaMapMarkerAlt />
              <span>Los Angeles</span>
            </div>
            <div className="area-item">
              <FaMapMarkerAlt />
              <span>San Fernando Valley</span>
            </div>
            <div className="area-item">
              <FaMapMarkerAlt />
              <span>Ventura County</span>
            </div>
            <div className="area-item">
              <FaMapMarkerAlt />
              <span>Burbank</span>
            </div>
            <div className="area-item">
              <FaMapMarkerAlt />
              <span>Glendale</span>
            </div>
            <div className="area-item">
              <FaMapMarkerAlt />
              <span>Pasadena</span>
            </div>
            <div className="area-item">
              <FaMapMarkerAlt />
              <span>Santa Clarita</span>
            </div>
            <div className="area-item">
              <FaMapMarkerAlt />
              <span>Thousand Oaks</span>
            </div>
          </div>
        </div>
      </section>

      {/* Second Reviews Section */}
      <section className="reviews-cta-section">
        <div className="container">
          <div className="reviews-cta-content">
            <div className="cta-text">
              <h2>Need Professional Demolition Services?</h2>
              <h3>We've Got You Covered!</h3>
              <p>
                From pool demolition with permitting to asbestos abatement -
                full-service demolition solutions.
              </p>
            </div>
            <div className="review-badges-inline">
              <img
                src="/images/google-reviews.svg"
                alt="Google Reviews"
                className="review-badge-img"
              />
              <img
                src="/images/yelp-reviews.svg"
                alt="Yelp Reviews"
                className="review-badge-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="contact-section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Get Your Free Demolition Quote</h2>
            <p>Contact us today for a free, no-obligation estimate</p>
          </div>

          <div className="contact-content">
            <div className="contact-info">
              <h3>Ready to Start Your Project?</h3>
              <p>
                Contact us now for a free, no-obligation quote. Our demolition
                experts are standing by to help with your residential or
                commercial project.
              </p>

              <div className="contact-details">
                <div className="contact-item">
                  <FaPhone className="contact-icon" />
                  <div>
                    <h4>Call Us Now</h4>
                    <a href="tel:8182207356" className="contact-phone">
                      (818) 220-7356
                    </a>
                  </div>
                </div>

                <div className="contact-item">
                  <FaClock className="contact-icon" />
                  <div>
                    <h4>Business Hours</h4>
                    <p>Monday - Saturday: 7:00 AM - 7:00 PM</p>
                    <p>Sunday: By Appointment</p>
                  </div>
                </div>

                <div className="contact-item">
                  <FaMapMarkerAlt className="contact-icon" />
                  <div>
                    <h4>Service Area</h4>
                    <p>Los Angeles & Ventura County</p>
                    <p>All Surrounding Communities</p>
                  </div>
                </div>
              </div>

              <div className="trust-badges">
                <div className="badge">
                  <FaShieldAlt />
                  <span>Licensed & Insured</span>
                </div>
                <div className="badge">
                  <FaStar />
                  <span>5-Star Rated</span>
                </div>
                <div className="badge">
                  <FaClock />
                  <span>Same-Day Service</span>
                </div>
              </div>
            </div>

            <div className="contact-form-wrapper">
              <form className="contact-form" name="quote-request" method="POST" data-netlify="true" onSubmit={handleSubmit}>
                <h3>Request Your Free Quote</h3>

                <div className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your Full Name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="(818) 555-0123"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email (Optional)</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">Project Details *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    placeholder="Tell us about your demolition project..."
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary btn-large">
                  Get A Free Quote
                </button>

                <p className="form-note">
                  We'll respond within 1 hour during business hours. All quotes
                  are 100% free with no obligation.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content text-center">
            <h2>Ready to Get Started?</h2>
            <p>Call now for immediate assistance and a free demolition quote</p>
            <a
              href="tel:8182207356"
              className="btn btn-primary btn-large btn-cta"
            >
              <FaPhone /> CALL NOW (818) 220-7356
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <img
                src="/images/logo-white.png"
                alt="Demo N Hauling Guys"
                className="footer-logo"
              />
              <p>
                Professional demolition services in Los Angeles since 2003.
                Licensed, insured, and trusted by hundreds of satisfied
                customers.
              </p>
              <div className="social-links">
                <a
                  href="https://www.facebook.com/profile.php?id=61571472643087&ref=PROFILE_EDIT_ig_profile_ac"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Facebook
                </a>
                <a
                  href="https://www.yelp.com/biz/demo-n-hauling-guys-san-fernando-valley"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Yelp
                </a>
                <a
                  href="https://share.google/QxesccJIEoBtI014h"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Google
                </a>
                <a
                  href="https://www.instagram.com/demoandhauling/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Instagram
                </a>
              </div>
            </div>

            <div className="footer-section">
              <h4>Demolition Services</h4>
              <ul>
                <li>
                  <a href="#services">Residential Demolition</a>
                </li>
                <li>
                  <a href="#services">Commercial Demolition</a>
                </li>
                <li>
                  <a href="#services">Pool Demolition & Permitting</a>
                </li>
                <li>
                  <a href="#services">Excavation & Grading</a>
                </li>
                <li>
                  <a href="#services">Asbestos Abatement</a>
                </li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li>
                  <a href="#about">About Us</a>
                </li>
                <li>
                  <a href="#services">Services</a>
                </li>
                <li>
                  <a href="#contact">Free Quote</a>
                </li>
                <li>
                  <a href="tel:8182207356">Call Now</a>
                </li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Contact Info</h4>
              <ul>
                <li>
                  <strong>Phone:</strong>{" "}
                  <a href="tel:8182207356">(818) 220-7356</a>
                </li>
                <li>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:info@demoguys.net">info@demoguys.net</a>
                </li>
                <li>
                  <strong>Service Area:</strong> Los Angeles & Ventura County
                </li>
                <li>
                  <strong>Hours:</strong> Mon-Sat 7AM-7PM
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2024 Demo Guys. All rights reserved.</p>
            <a
              href="https://softangeles.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '14px', opacity: 0.9, fontSize: '15px', fontWeight: 500, textDecoration: 'none', color: '#ffffff' }}
            >
              <span>Powered by</span>
              <img src="https://softangeles.com/logo-wide.png" alt="SoftAngeles" style={{ height: '30px', width: 'auto' }} />
            </a>
            <div className="footer-links">
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
              <a href="#">Sitemap</a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default LandingPage;
